import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  Button,
  FormControl,
  Stack,
  Card,
  CardContent,
  Divider,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import ReplayIcon from '@mui/icons-material/Replay';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useOrderActions } from 'src/context/providers/OrderContext';
import { usePaymentActions } from 'src/context/providers/PaymentContext';
import LinkIcon from '@mui/icons-material/Link';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import { PaymentMethod, PaymentMethodLabels } from 'src/utils/payment-methods';
import { jwtDecode } from 'jwt-decode';

const ALL_PAYMENT_METHODS = [
  {
    value: PaymentMethod.CASH,
    label: PaymentMethodLabels[PaymentMethod.CASH],
    icon: <AttachMoneyIcon />,
    color: 'success',
    locations: ['USA'],
  },
  {
    value: PaymentMethod.STRIPE_LINK,
    label: PaymentMethodLabels[PaymentMethod.STRIPE_LINK],
    icon: <LinkIcon />,
    color: 'info',
    locations: ['USA'],
  },
  {
    value: PaymentMethod.STRIPE_TERMINAL,
    label: PaymentMethodLabels[PaymentMethod.STRIPE_TERMINAL],
    icon: <PointOfSaleIcon />,
    color: 'warning',
    locations: ['USA'],
  },
  {
    value: PaymentMethod.BOG_TERMINAL,
    label: PaymentMethodLabels[PaymentMethod.BOG_TERMINAL],
    icon: <PointOfSaleIcon />,
    color: 'secondary',
    locations: ['Georgia'],
  },
  {
    value: PaymentMethod.BANK_TRANSFER,
    label: PaymentMethodLabels[PaymentMethod.BANK_TRANSFER],
    icon: <AccountBalanceIcon />,
    color: 'info',
    locations: ['Georgia'],
  },
  {
    value: PaymentMethod.CHECK,
    label: PaymentMethodLabels[PaymentMethod.CHECK],
    icon: <DescriptionIcon />,
    color: 'default',
    locations: ['USA'],
  },
];

const isBucketPaid = (b) => Boolean(b?.group?.payed) || b?.group?.pay_status === 'PAID';

const calculateBucketFallbackTotal = (bucket) => {
  const grp = bucket?.group || {};
  const pickupPrice = Number(grp.pickup_price) || 0;
  const promotion = Number(grp.promotion) || 0;
  const subtotal = (bucket?.orders || []).reduce((sum, o) => sum + (Number(o?.price) || 0), 0);
  return subtotal + pickupPrice - promotion;
};

const PaymentDrawer = ({
  isDrawerOpen,
  handleDrawerToggle,
  buckets,
  onPaymentSubmit,
  onTerminalCancel,
  onReprocessComplete,
  loading = false,
}) => {
  const { getGroupCalculatedPrices } = useOrderActions();
  const { cancelTerminalPaymentIntent, initiateGroupPayment } = usePaymentActions();

  const [paymentMethod, setPaymentMethod] = useState('');
  const [currency, setCurrency] = useState(null);
  const [errors, setErrors] = useState({});
  const [pricesByGroup, setPricesByGroup] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [cancelLoadingByGroup, setCancelLoadingByGroup] = useState({});
  const [reprocessLoadingByGroup, setReprocessLoadingByGroup] = useState({});

  const getAdminLocation = () => {
    try {
      const auth = JSON.parse(localStorage.getItem('token'));
      if (auth) {
        const decoded = jwtDecode(auth);
        return decoded?.location || null;
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
    return null;
  };

  const adminLocation = getAdminLocation();
  const PAYMENT_METHODS = ALL_PAYMENT_METHODS.filter((method) => {
    if (!adminLocation) return true;
    return method.locations.includes(adminLocation);
  });

  const safeBuckets = useMemo(() => buckets || [], [buckets]);

  const unpaidBuckets = useMemo(() => safeBuckets.filter((b) => !isBucketPaid(b)), [safeBuckets]);

  const allBucketsPaid = safeBuckets.length > 0 && unpaidBuckets.length === 0;

  const groupIdsKey = useMemo(() => safeBuckets.map((b) => b?.groupId).join(','), [safeBuckets]);

  const aggregateTotals = useMemo(() => {
    let totalUSD = 0;
    let totalGEL = 0;
    let totalWeight = 0;

    safeBuckets.forEach((bucket) => {
      const prices = pricesByGroup[bucket?.groupId]?.calculatedPrices;
      if (prices) {
        totalUSD += Number(prices.totalUSD) || 0;
        totalGEL += Number(prices.totalGEL) || 0;
      } else {
        totalUSD += calculateBucketFallbackTotal(bucket);
      }
      totalWeight += (bucket?.orders || []).reduce((sum, o) => sum + (Number(o?.weight) || 0), 0);
    });

    return { totalUSD, totalGEL, totalWeight };
  }, [safeBuckets, pricesByGroup]);

  useEffect(() => {
    if (!isDrawerOpen) return;

    setCurrency(adminLocation === 'USA' ? 'USD' : 'GEL');

    if (safeBuckets.length === 0) return;

    const fetchAllPrices = async () => {
      setLoadingPrices(true);
      try {
        const results = await Promise.all(
          safeBuckets.map(async (b) => {
            if (!b?.groupId) return [null, null];
            try {
              const prices = await getGroupCalculatedPrices(b.groupId);
              return [b.groupId, prices];
            } catch (err) {
              console.error(`Error fetching prices for ${b.groupId}:`, err);
              return [b.groupId, null];
            }
          })
        );
        const map = {};
        results.forEach(([id, prices]) => {
          if (id != null && prices) map[id] = prices;
        });
        setPricesByGroup(map);
      } finally {
        setLoadingPrices(false);
      }
    };

    fetchAllPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, groupIdsKey]);

  const handlePaymentMethodChange = (event) => {
    const newMethod = event.target.value;
    setPaymentMethod(newMethod);
    setErrors((prev) => ({ ...prev, paymentMethod: '' }));

    if (newMethod === PaymentMethod.STRIPE_LINK || newMethod === PaymentMethod.STRIPE_TERMINAL) {
      if (currency !== 'USD') setCurrency('USD');
    }
  };

  const handleCurrencyChange = (_e, newCurrency) => {
    if (!newCurrency) return;
    setCurrency(newCurrency);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!paymentMethod) {
      newErrors.paymentMethod = 'Please select a payment method';
    }
    if (unpaidBuckets.length === 0) {
      newErrors.paymentMethod = 'No unpaid groups to process';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getBucketAmount = useCallback(
    (bucket) => {
      const prices = pricesByGroup[bucket?.groupId]?.calculatedPrices;
      if (prices) {
        if (currency === 'USD' && prices.totalUSD != null) return Number(prices.totalUSD);
        if (currency === 'GEL' && prices.totalGEL != null) return Number(prices.totalGEL);
      }
      return calculateBucketFallbackTotal(bucket);
    },
    [pricesByGroup, currency]
  );

  const handleClose = () => {
    setPaymentMethod('');
    setCurrency(adminLocation === 'USA' ? 'USD' : 'GEL');
    setPricesByGroup({});
    setLoadingPrices(false);
    setCancelLoadingByGroup({});
    setReprocessLoadingByGroup({});
    setErrors({});
    handleDrawerToggle();
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const totalAmount = currency === 'USD' ? aggregateTotals.totalUSD : aggregateTotals.totalGEL;

    onPaymentSubmit?.({
      paymentMethod,
      currency,
      totalAmount,
      buckets: unpaidBuckets.map((b) => ({
        groupId: b?.groupId,
        amount: getBucketAmount(b),
        orders: b?.orders || [],
        group: b?.group || {},
        calculatedPrices: pricesByGroup[b?.groupId]?.calculatedPrices,
      })),
    });
  };

  const handleCancelTerminal = async (bucket) => {
    const intentId = bucket?.group?.paymentIntentId;
    const id = bucket?.groupId;
    if (!intentId || !id) return;
    try {
      setCancelLoadingByGroup((prev) => ({ ...prev, [id]: true }));
      await cancelTerminalPaymentIntent({ paymentIntentId: intentId });
      onTerminalCancel?.(bucket);
      handleClose();
    } finally {
      setCancelLoadingByGroup((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleReprocessPayment = async (bucket) => {
    const id = bucket?.groupId;
    const method = bucket?.group?.paymentMethod;
    if (!id || !method) return;
    try {
      setReprocessLoadingByGroup((prev) => ({ ...prev, [id]: true }));
      const result = await initiateGroupPayment({
        group_id: id,
        currency: 'USD',
        paymentMethod: method,
      });
      if (result) onReprocessComplete?.(bucket);
    } finally {
      setReprocessLoadingByGroup((prev) => ({ ...prev, [id]: false }));
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        position: 'fixed',
        zIndex: 2,
        top: 0,
        left: 0,
        display: isDrawerOpen ? 'block' : 'none',
        cursor: 'pointer',
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') handleClose();
      }}
    >
      <Drawer
        variant="persistent"
        anchor="right"
        open={isDrawerOpen}
        onClose={handleClose}
        PaperProps={{
          sx: { width: { lg: '40%', xs: '100%', md: '100%' } },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderBottom: '1px solid rgba(220, 220, 220, 0.2)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Payment Processing</Typography>
            <Chip
              size="small"
              label={`${safeBuckets.length} group${safeBuckets.length !== 1 ? 's' : ''}`}
            />
            {allBucketsPaid && <Chip color="success" size="small" label="All Paid" />}
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ padding: '24px', height: '100%', overflow: 'auto' }}>
          <Stack spacing={3}>
            {loadingPrices && (
              <Box>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                  Loading calculated prices...
                </Typography>
              </Box>
            )}

            {/* Per-bucket summaries */}
            {safeBuckets.map((bucket) => {
              const groupId = bucket?.groupId;
              const prices = pricesByGroup[groupId]?.calculatedPrices;
              const paid = isBucketPaid(bucket);
              const groupPaymentMethod = bucket?.group?.paymentMethod;
              const groupPaymentIntentId = bucket?.group?.paymentIntentId;
              const isPendingStripe =
                Boolean(groupPaymentIntentId) &&
                !paid &&
                (groupPaymentMethod === PaymentMethod.STRIPE_LINK ||
                  groupPaymentMethod === PaymentMethod.STRIPE_TERMINAL);
              const bucketWeight = (bucket?.orders || []).reduce(
                (sum, o) => sum + (Number(o?.weight) || 0),
                0
              );

              return (
                <Card key={groupId ?? `bucket-${Math.random()}`}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6">Group {groupId ? `#${groupId}` : 'N/A'}</Typography>
                      {paid && <Chip color="success" size="small" label="Paid" />}
                      {!paid && isPendingStripe && (
                        <Chip color="warning" size="small" label="Pending" variant="outlined" />
                      )}
                    </Box>

                    <Stack spacing={1}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography>Orders Count:</Typography>
                        <Typography fontWeight="bold">{bucket?.orders?.length || 0}</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography>Total Weight (kg):</Typography>
                        <Typography fontWeight="bold">{bucketWeight.toFixed(2)}</Typography>
                      </Box>

                      {!!bucket?.orders?.length && (
                        <>
                          <Divider />
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
                            Orders
                          </Typography>
                          <Stack spacing={1}>
                            {bucket.orders.map((order, idx) => (
                              <Box
                                key={`${order?.tracking_number || idx}-${idx}`}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr auto',
                                  gap: 2,
                                  alignItems: 'center',
                                  px: 1.5,
                                  py: 1,
                                  borderRadius: 1,
                                  bgcolor: 'action.hover',
                                  border: (theme) => `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    sx={{ fontFamily: 'monospace' }}
                                    noWrap
                                  >
                                    {order?.tracking_number || 'No Tracking'}
                                  </Typography>
                                  {order?.name && (
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {order.name}
                                    </Typography>
                                  )}
                                </Box>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={Number(order?.weight) ? `${Number(order.weight)} kg` : '—'}
                                />
                              </Box>
                            ))}
                          </Stack>
                        </>
                      )}

                      {prices && (
                        <>
                          <Divider />
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
                            Price Breakdown
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography variant="body2">Transportation (USD):</Typography>
                            <Typography variant="body2">${prices.transportationUSD}</Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography variant="body2">Pickup Price (USD):</Typography>
                            <Typography variant="body2" color="primary">
                              ${prices.pickupPriceUSD}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography variant="body2">Delivery (USD):</Typography>
                            <Typography variant="body2">${prices.deliveryUSD}</Typography>
                          </Box>
                          {prices.extraChargesUSD != null && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography variant="body2">Extra (USD):</Typography>
                              <Typography variant="body2">${prices.extraChargesUSD}</Typography>
                            </Box>
                          )}
                          {prices.promotionApplied > 0 && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography variant="body2">Promotion Applied:</Typography>
                              <Typography variant="body2" color="success.main">
                                -${prices.promotionApplied}
                              </Typography>
                            </Box>
                          )}

                          <Divider />

                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography variant="subtitle1">Subtotal (USD):</Typography>
                            <Typography variant="subtitle1" color="primary" fontWeight="bold">
                              ${prices.totalUSD}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography variant="subtitle1">Subtotal (GEL):</Typography>
                            <Typography variant="subtitle1" color="secondary" fontWeight="bold">
                              {prices.totalGEL} ₾
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Exchange Rate: {prices.exchangeRate}
                          </Typography>
                        </>
                      )}

                      {!prices && !loadingPrices && (
                        <>
                          <Divider />
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography variant="subtitle1">Subtotal:</Typography>
                            <Typography variant="subtitle1" color="primary" fontWeight="bold">
                              ${calculateBucketFallbackTotal(bucket)}
                            </Typography>
                          </Box>
                        </>
                      )}

                      {/* Per-bucket pending stripe actions */}
                      {isPendingStripe && (
                        <Box sx={{ mt: 1 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Typography variant="body2" color="text.secondary">
                              Method:{' '}
                              <Typography component="span" variant="body2" fontWeight="bold">
                                {PaymentMethodLabels[groupPaymentMethod] ?? groupPaymentMethod}
                              </Typography>
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<ReplayIcon />}
                              onClick={() => handleReprocessPayment(bucket)}
                              disabled={reprocessLoadingByGroup[groupId] || loading}
                            >
                              {reprocessLoadingByGroup[groupId] ? 'Processing…' : 'Re-process'}
                            </Button>
                            {groupPaymentMethod === PaymentMethod.STRIPE_TERMINAL && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={() => handleCancelTerminal(bucket)}
                                disabled={cancelLoadingByGroup[groupId]}
                              >
                                {cancelLoadingByGroup[groupId] ? 'Canceling...' : 'Cancel Terminal'}
                              </Button>
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}

            {/* Grand total */}
            <Divider sx={{ my: 1 }}>
              <Chip label="Total" size="large" color="primary" />
            </Divider>
            {safeBuckets.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography>Groups:</Typography>
                      <Typography fontWeight="bold">{safeBuckets.length}</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography>Total Weight (kg):</Typography>
                      <Typography fontWeight="bold">
                        {aggregateTotals.totalWeight.toFixed(2)}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6">Total (USD):</Typography>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        ${aggregateTotals.totalUSD.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6">Total (GEL):</Typography>
                      <Typography variant="h6" color="secondary" fontWeight="bold">
                        {aggregateTotals.totalGEL.toFixed(2)} ₾
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Payment Method Selection */}
            {!allBucketsPaid && unpaidBuckets.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Select Payment Method
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Applies to {unpaidBuckets.length} unpaid group
                    {unpaidBuckets.length !== 1 ? 's' : ''}.
                  </Typography>
                  <FormControl fullWidth error={!!errors.paymentMethod}>
                    <RadioGroup value={paymentMethod} onChange={handlePaymentMethodChange}>
                      {PAYMENT_METHODS.map((method) => (
                        <FormControlLabel
                          key={method.value}
                          value={method.value}
                          control={<Radio />}
                          label={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {method.icon}
                              <Typography>{method.label}</Typography>
                              <Chip
                                label={method.label}
                                color={method.color}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          }
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </RadioGroup>
                    {errors.paymentMethod && (
                      <Typography variant="caption" color="error">
                        {errors.paymentMethod}
                      </Typography>
                    )}
                  </FormControl>
                </CardContent>
              </Card>
            )}

            {/* Payment Details */}
            {!allBucketsPaid && unpaidBuckets.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Payment Details
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <RadioGroup
                      row
                      value={currency}
                      onChange={(e) => handleCurrencyChange(e, e.target.value)}
                    >
                      {adminLocation === 'USA' && (
                        <FormControlLabel value="USD" control={<Radio />} label="USD" />
                      )}
                      {adminLocation === 'Georgia' &&
                        paymentMethod !== PaymentMethod.STRIPE_LINK &&
                        paymentMethod !== PaymentMethod.STRIPE_TERMINAL && (
                          <FormControlLabel value="GEL" control={<Radio />} label="GEL" />
                        )}
                    </RadioGroup>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-end',
                mt: 'auto',
              }}
            >
              <Button variant="outlined" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              {!allBucketsPaid && unpaidBuckets.length > 0 && (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading || !paymentMethod}
                  startIcon={<PaymentIcon />}
                >
                  {loading ? 'Processing...' : `Process Payment (${unpaidBuckets.length})`}
                </Button>
              )}
            </Box>
          </Stack>
        </Box>
      </Drawer>
    </div>
  );
};

PaymentDrawer.propTypes = {
  isDrawerOpen: PropTypes.bool.isRequired,
  handleDrawerToggle: PropTypes.func.isRequired,
  buckets: PropTypes.arrayOf(
    PropTypes.shape({
      groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      group: PropTypes.shape({
        pickup_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        promotion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        payed: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
        pay_status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        paymentMethod: PropTypes.string,
        paymentIntentId: PropTypes.string,
      }),
      orders: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          price_gz: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          tracking_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
      ),
    })
  ),
  onPaymentSubmit: PropTypes.func,
  onTerminalCancel: PropTypes.func,
  onReprocessComplete: PropTypes.func,
  loading: PropTypes.bool,
};

PaymentDrawer.defaultProps = {
  buckets: [],
  onPaymentSubmit: undefined,
  onTerminalCancel: undefined,
  onReprocessComplete: undefined,
  loading: false,
};

export default PaymentDrawer;
